import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import DOMPurify from 'dompurify';
import { produce } from 'immer';
import { from, concatMap, forkJoin, of, toArray } from 'rxjs';
import { GuidelineService, MessagesService, UnitService } from 'shared/domain';
import { DialogService } from 'shared/ui-common';
import { OrganisationSettingsService, SanitizeHtmlPipe, SharedUtilCommonModule } from 'shared/util-common';

import {
  FlowchartDecisionNodeDto,
  FlowchartLinkDto,
  FlowchartLinkValueDto,
  FlowchartLinkValueType,
  GuidelineComponentType,
  GuidelineObservationDto,
  UnitDto,
} from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { CustomisableFlowchartElementComponent } from '../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/customisable-flowchart-element/customisable-flowchart-element.component';
import { FlowchartNodeConfig } from '../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/flowchart-customisations-settings-tab.component';
import { DividerComponent } from '../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { RuleInputDialogComponent } from '../../../../../../../shared/ui-common/src/lib/components/rule-input/rule-input-dialog/rule-input-dialog.component';
import { LegacySidebarService } from '../../../infrastructure/legacy-sidebar.service';
import { TitleDescriptionComponent } from '../../chapter-sidebar-wrapper/components/title-description/title-description.component';
import { GuidelineSidebarBasics, GuidelineSidebarBasicsComponent } from '../../guideline-sidebar-basics/guideline-sidebar-basics.component';
import { TechnicalInfoComponent } from '../../sub-components/technical-info/technical-info.component';
import { UnitMolarComponent } from '../../sub-components/unit-molar/unit-molar.component';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    TranslocoModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatDividerModule,
    MatTabsModule,
    TitleDescriptionComponent,
    UnitMolarComponent,
    TechnicalInfoComponent,
    SharedUtilCommonModule,
    SanitizeHtmlPipe,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
    CustomisableFlowchartElementComponent,
  ],
  selector: 'flowchart-sidebar-decision',
  templateUrl: './flowchart-sidebar-decision.component.html',
  styleUrls: ['./flowchart-sidebar-decision.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowchartSidebarDecisionComponent {
  private dialogService = inject(DialogService);
  private sidebarService = inject(LegacySidebarService);
  public unitService = inject(UnitService);
  private messagesService = inject(MessagesService);
  private organisationSettingsService = inject(OrganisationSettingsService);
  private guidelineService = inject(GuidelineService);

  public sidebarElement = input<FlowchartDecisionNodeDto | undefined>(undefined);

  protected guidelineComponent = computed(() => {
    return this.sidebarElement().guidelineCriteria ?? this.sidebarElement().guidelineObservation;
  });

  public flowchartElementChanged = output<FlowchartDecisionNodeDto>();

  protected organisationSettings = computed(
    () => this.guidelineService.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  protected colorPresets = computed(() => this.organisationSettings().customisations?.colorPresets ?? []);

  protected hasColorPresets = computed(() => {
    return this.colorPresets().length > 0;
  });

  protected flowchartNodeConfig = computed<FlowchartNodeConfig>(() => {
    const colorPresets = this.colorPresets();
    const organisationCustomisations = this.organisationSettings().customisations?.flowchart;
    const elementColorPreset = this.sidebarElement().colorPresetId;

    const colorPreset = colorPresets.find((preset) => preset.id === (elementColorPreset ?? organisationCustomisations?.decision));

    return {
      type: 'decision',
      content: 'Decision',
      colorPreset,
    };
  });

  protected onTitleChanged(title: string) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        if (this.guidelineComponent().componentType === GuidelineComponentType.Criteria) {
          draft.guidelineCriteria = {
            ...draft.guidelineCriteria,
            title,
          };
        } else if (this.guidelineComponent().componentType === GuidelineComponentType.Observation) {
          draft.guidelineObservation = {
            ...draft.guidelineObservation,
            title,
          };
        }
      }),
    );
  }

  protected onDescriptionChanged(description: string) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        if (this.guidelineComponent().componentType === GuidelineComponentType.Criteria) {
          draft.guidelineCriteria = {
            ...draft.guidelineCriteria,
            description,
          };
        } else if (this.guidelineComponent().componentType === GuidelineComponentType.Observation) {
          draft.guidelineObservation = {
            ...draft.guidelineObservation,
            description,
          };
        }
      }),
    );
  }

  protected onRuleTitleChanged(ruleTitle: string) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        draft.ruleTitle = ruleTitle;
      }),
    );
  }

  protected onRuleDescriptionChanged(ruleDescription: string) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        draft.ruleDescription = ruleDescription;
      }),
    );
  }

  protected onShowDescriptionChange(showDescription: boolean) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        draft.showDescription = showDescription;
      }),
    );
  }

  protected onColorPresetChanged(colorPresetId: string): void {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        draft.colorPresetId = colorPresetId;
      }),
    );
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    this.flowchartElementChanged.emit(
      produce(this.sidebarElement(), (draft) => {
        draft.references = basics.references;
        draft.links = basics.links;
        draft.resourceTags = basics.resourceTags;

        if (this.guidelineComponent().componentType === GuidelineComponentType.Criteria) {
          draft.attachments = basics.attachments;
        } else if (this.guidelineComponent().componentType === GuidelineComponentType.Observation) {
          draft.attachments = basics.attachments;
        }
      }),
    );
  }

  private getLinkEntry(link: FlowchartLinkDto): [string, any] | undefined {
    const values = link.attribute?.value;
    if (!values) return undefined;
    const entry = Object.entries(values).find(([_, value]) => value !== undefined && value !== null && value !== '');
    return entry ? [entry[0], entry[1]] : undefined;
  }

  protected onUnitMolarChanged(unit: UnitDto) {
    if (!unit) {
      return;
    }

    const latestUnit = this.getLatestUnit();
    const molarMass = this.sidebarElement().guidelineObservation?.observationDefinition.molarMass;

    const linkEntries = this.sidebarElement()
      .outgoingLinks.filter((link) => {
        return link.attribute;
      })
      .map((link) => this.getLinkEntry(link));

    from(linkEntries)
      .pipe(
        concatMap(([linkType, linkValue]) => {
          if (linkType === FlowchartLinkValueType.numericalValueRange) {
            const range = linkValue as FlowchartLinkValueDto['numericalValueRange'];
            return forkJoin([
              this.unitService.getConvertedUnit(parseFloat(range.item1.toString()), latestUnit, unit.abbreviation, molarMass),
              this.unitService.getConvertedUnit(parseFloat(range.item2.toString()), latestUnit, unit.abbreviation, molarMass),
            ]).pipe(
              concatMap(([res1, res2]) => {
                return of([res1, res2]);
              }),
            );
          } else {
            return this.unitService.getConvertedUnit(parseFloat(linkValue), latestUnit, unit.abbreviation, molarMass).pipe(
              concatMap((res) => {
                return of(res);
              }),
            );
          }
        }),
        toArray(),
      )
      .subscribe({
        next: (convertedLinks) => {
          const updatedLinks: FlowchartLinkDto[] = this.sidebarElement().outgoingLinks.map((link, idx) => {
            if (!link.attribute) {
              return link;
            }

            const linkEntry = Object.entries(link.attribute.value).find(([_, value]) => this.sidebarService.isValueSet(value));
            const [linkType] = linkEntry || [];

            if (linkType === FlowchartLinkValueType.numericalValueRange) {
              return {
                ...link,
                attribute: {
                  ...link.attribute,
                  value: {
                    ...link.attribute.value,
                    numericalValueRange: {
                      item1: convertedLinks[idx][0],
                      item2: convertedLinks[idx][1],
                    },
                  },
                },
              };
            } else {
              const numericalValue = convertedLinks[idx] as number;

              return {
                ...link,
                attribute: {
                  ...link.attribute,
                  value: {
                    ...link.attribute.value,
                    numericalValue,
                  },
                },
              };
            }
          });

          const newFlowchartDecision = produce(this.sidebarElement(), (draft) => {
            draft.outgoingLinks = updatedLinks;
            draft.guidelineObservation = {
              ...draft.guidelineObservation,
              observationDefinition: {
                ...draft.guidelineObservation?.observationDefinition,
                unitId: unit.id,
                unit,
              },
              unit,
              unitId: unit.id,
            };
          });

          this.flowchartElementChanged.emit(newFlowchartDecision);
        },
        error: () => {
          this.messagesService.showError('CouldNotGetConvertedUnit');
        },
      });
  }

  protected editRule() {
    const ruleDialog = this.dialogService.open(RuleInputDialogComponent, {
      width: '60%',
      data: {
        label: 'Definition',
        rule: this.sidebarElement().ruleTitle ?? '',
        prettyfiedRule: this.sidebarElement().rulePrettyfied,
      },
    });

    ruleDialog.afterClosed().subscribe(({ rule, prettified }) => {
      if (!rule || !prettified) {
        return;
      }

      //only set sidebar and node title to new rule if title has not been changed
      if (this.sidebarElement().ruleTitle === this.sanitizeHtml(this.sidebarElement().rulePrettyfied)) {
        this.flowchartElementChanged.emit(
          produce(this.sidebarElement(), (draft) => {
            draft.ruleTitle = this.sanitizeHtml(prettified);
            draft.rulePrettyfied = prettified;
          }),
        );
      }
    });
  }

  private getLatestUnit(): string {
    const guidelineComponent = this.guidelineComponent() as GuidelineObservationDto;
    return guidelineComponent?.unit?.abbreviation
      ? guidelineComponent?.unit?.abbreviation
      : guidelineComponent?.observationDefinition?.unit?.abbreviation;
  }

  private sanitizeHtml(text: string) {
    const sanitized = DOMPurify.sanitize(text, {
      ALLOWED_ATTR: ['class', 'style', 'id'],
      RETURN_DOM: true,
    });
    return sanitized.textContent;
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
  protected readonly GuidelineComponentType = GuidelineComponentType;
}
